import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAgreementTypes,
  updateAgreementType,
  createAgreementType,
  deleteAgreementType,
} from "../../api/service/settings";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
};

export const agreementTypesController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteAgreementTypeAsync = createAsyncThunk(
  "agreement_types/deleteAgreementTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteAgreementType(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_agreement_type");
    }
  }
);

export const createAgreementTypeAsync = createAsyncThunk(
  "agreement_types/createAgreementTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createAgreementType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "create_agreement_type"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_agreement_type");
    }
  }
);

export const updateAgreementTypeAsync = createAsyncThunk(
  "agreement_types/updateReportTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateAgreementType(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "update_agreement_type"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_agreement_type");
    }
  }
);

export const getAgreementTypesAsync = createAsyncThunk(
  "agreement_types/getAgreementTypesAsync",
  async (controller = agreementTypesController, { rejectWithValue }) => {
    try {
      const response = await fetchAgreementTypes(controller);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_agreement_types"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_agreement_types");
    }
  }
);

const agreementTypeSlice = createSlice({
  name: "agreement_types",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAgreementTypesAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getAgreementTypesAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(getAgreementTypesAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke hente avtaletypene."
        );
      })
      .addCase(updateAgreementTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateAgreementTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateAgreementTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere avtaletypen."
        );
      })
      .addCase(createAgreementTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createAgreementTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createAgreementTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke opprette avtaletypen."
        );
      })
      .addCase(deleteAgreementTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteAgreementTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteAgreementTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke slette avtaletypen."
        );
      });
  },
});

export const { reducer } = agreementTypeSlice;

export default agreementTypeSlice;

export const selectAgreementTypeById = (state, agreementTypeId) =>
  state.agreement_types.data.find((type) => type.id == agreementTypeId);
